import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Eye, ArrowUpRight, Inbox, MailCheck, CheckCircle2, RefreshCw, ServerCog, CheckCheck, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function AdminOverviewPage() {
    const [leads, setLeads] = useState({ total: 0, new: 0, contacted: 0, qualified: 0 });
    const [analytics, setAnalytics] = useState(null);
    const [blogCount, setBlogCount] = useState(0);
    const [diagnostics, setDiagnostics] = useState(null);
    const [reseeding, setReseeding] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);

    const loadDiagnostics = useCallback(async () => {
        try {
            const r = await api.get('/diagnostics/content');
            setDiagnostics(r.data || null);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') console.debug('[Diagnostics] load failed:', err);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get('/admin/leads/stats'),
            api.get('/admin/analytics/overview', { params: { days: 30 } }),
            api.get('/admin/blog'),
        ]).then(([a, b, c]) => {
            if (cancelled) return;
            setLeads(a.data || {});
            setAnalytics(b.data || null);
            setBlogCount(c.data?.count || 0);
        }).catch((err) => {
            if (process.env.NODE_ENV !== 'production') console.debug('[AdminOverview] load failed:', err);
        });
        loadDiagnostics();
        return () => { cancelled = true; };
    }, [loadDiagnostics]);

    const runReseed = useCallback(async (overwrite = false) => {
        try {
            setReseeding(true);
            const res = await api.post('/admin/reseed', null, { params: overwrite ? { overwrite: 'true' } : {} });
            setDiagnostics(res?.data || null);
            setLastSyncedAt(new Date());
            const posts = res?.data?.blog_posts ?? 0;
            const seo = res?.data?.seo_entries ?? 0;
            toast.success(
                overwrite
                    ? `Force sync complete. Latest defaults applied to site content and ${seo} SEO entr${seo === 1 ? 'y' : 'ies'}.`
                    : `Content synced. ${posts} blog post${posts === 1 ? '' : 's'}, ${seo} SEO entr${seo === 1 ? 'y' : 'ies'}.`
            );
            // Refresh blog count widget too
            try {
                const b = await api.get('/admin/blog');
                setBlogCount(b.data?.count || 0);
            } catch { /* non-blocking */ }
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Reseed failed');
        } finally {
            setReseeding(false);
        }
    }, []);

    const cards = [
        { label: 'Total Leads', value: leads.total || 0, icon: Users, to: '/admin/leads', accent: 'cyan' },
        { label: 'New Leads', value: leads.new || 0, icon: Inbox, to: '/admin/leads', accent: 'lime' },
        { label: 'Blog Posts', value: blogCount, icon: FileText, to: '/admin/blog', accent: 'cyan' },
        { label: 'Page Views (30d)', value: analytics?.totals?.window || 0, icon: Eye, to: '/admin/analytics', accent: 'lime' },
    ];

    return (
        <div data-testid="admin-overview" className="space-y-8">
            <header>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1">Here’s what’s happening across DigiTech Cloud AI.</p>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {cards.map((c) => (
                    <Link key={c.label} to={c.to} className="group dt-glass rounded-xl p-4 sm:p-5 hover:border-primary/40 transition-colors">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{c.label}</span>
                            <c.icon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="mt-2 flex items-end justify-between">
                            <span className="text-3xl font-semibold font-display">{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</span>
                            <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                        </div>
                    </Link>
                ))}
            </div>

            {/* Deploy sync panel — one-click reseed for stale live sites */}
            <SyncContentPanel
                diagnostics={diagnostics}
                reseeding={reseeding}
                onSync={() => runReseed(false)}
                onForceSync={() => {
                    if (window.confirm('Force sync will overwrite the current site content and SEO metadata with the latest defaults. Any CMS edits to those pages will be replaced. Continue?')) {
                        runReseed(true);
                    }
                }}
                onRefresh={loadDiagnostics}
                lastSyncedAt={lastSyncedAt}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="lg:col-span-2 dt-glass rounded-xl p-5">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-semibold">Lead pipeline</h2>
                        <Link to="/admin/leads" className="text-xs text-primary hover:text-accent inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></Link>
                    </div>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            { label: 'New', value: leads.new, icon: Inbox },
                            { label: 'Contacted', value: leads.contacted, icon: MailCheck },
                            { label: 'Qualified', value: leads.qualified, icon: CheckCircle2 },
                            { label: 'Total', value: leads.total, icon: Users },
                        ].map((s) => (
                            <div key={s.label} className="rounded-lg border border-white/10 bg-white/5 p-3">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground"><s.icon className="h-3.5 w-3.5" /> {s.label}</div>
                                <div className="mt-1 text-2xl font-semibold font-display">{s.value || 0}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="dt-glass rounded-xl p-5">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-semibold">Traffic snapshot</h2>
                        <Link to="/admin/analytics" className="text-xs text-primary hover:text-accent inline-flex items-center gap-1">Details <ArrowUpRight className="h-3 w-3" /></Link>
                    </div>
                    <div className="mt-4 space-y-3 text-sm">
                        <Row label="Today" value={analytics?.totals?.today || 0} />
                        <Row label="Last 24h" value={analytics?.totals?.last_24h || 0} />
                        <Row label="Last 7d" value={analytics?.totals?.last_7d || 0} />
                        <Row label="Unique sessions (30d)" value={analytics?.totals?.unique_sessions || 0} />
                        <Row label="Mobile share" value={`${analytics?.totals?.mobile_share || 0}%`} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function SyncContentPanel({ diagnostics, reseeding, onSync, onForceSync, onRefresh, lastSyncedAt }) {
    const emptyBlog = diagnostics && diagnostics.blog_posts === 0;
    const emptySEO = diagnostics && diagnostics.seo_entries === 0;
    const missingContent = diagnostics && !diagnostics.has_site_content;
    const stale = emptyBlog || emptySEO || missingContent;

    return (
        <section
            data-testid="admin-sync-panel"
            className={`dt-glass rounded-xl p-5 ${stale ? 'border-amber-400/40' : ''}`}
        >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3">
                    <span className={`h-10 w-10 rounded-lg inline-flex items-center justify-center border ${stale ? 'bg-amber-400/10 border-amber-400/40 text-amber-300' : 'bg-primary/10 border-primary/30 text-primary'}`}>
                        {stale ? <AlertTriangle className="h-5 w-5" /> : <ServerCog className="h-5 w-5" />}
                    </span>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-display text-lg font-semibold">Deploy sync</h2>
                            {stale ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-400/10 border border-amber-400/40 text-amber-300 px-2 py-0.5 rounded-full">
                                    Content missing
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-primary/10 border border-primary/30 text-primary px-2 py-0.5 rounded-full">
                                    <CheckCheck className="h-3 w-3" /> Live content healthy
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-2xl">
                            After a redeploy, one click re-runs the seed and restores any blog posts, SEO entries and default site
                            content that didn’t make it through — without touching pages you’ve already customised in the CMS.
                        </p>
                        {lastSyncedAt && (
                            <div className="mt-1.5 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                                Last synced · {lastSyncedAt.toLocaleString()}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onRefresh}
                        disabled={reseeding}
                        className="text-foreground/80"
                        data-testid="admin-sync-refresh"
                        title="Refresh diagnostics"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={onSync}
                        disabled={reseeding}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="admin-sync-button"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${reseeding ? 'animate-spin' : ''}`} />
                        {reseeding ? 'Syncing…' : 'Sync content'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={onForceSync}
                        disabled={reseeding}
                        className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
                        data-testid="admin-force-sync-button"
                        title="Overwrite CMS content + SEO with the latest coded defaults"
                    >
                        Force sync
                    </Button>
                </div>
            </div>

            {/* Diagnostics grid */}
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <DiagCell label="Blog posts"        value={diagnostics?.blog_posts}          warn={emptyBlog} />
                <DiagCell label="SEO entries"       value={diagnostics?.seo_entries}         warn={emptySEO} />
                <DiagCell label="Site content"      value={diagnostics?.has_site_content ? 'OK' : '—'} warn={missingContent} />
                <DiagCell label="Uploads"           value={diagnostics?.uploads} />
                <DiagCell label="Brand overrides"   value={diagnostics?.brand_image_overrides} />
                <DiagCell label="Gallery items"     value={diagnostics?.gallery_items} />
            </div>
        </section>
    );
}

function DiagCell({ label, value, warn }) {
    const display = value === undefined || value === null ? '…' : (typeof value === 'number' ? value.toLocaleString() : value);
    return (
        <div className={`rounded-lg border p-3 ${warn ? 'border-amber-400/40 bg-amber-400/5' : 'border-white/10 bg-white/5'}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`mt-1 text-lg font-semibold font-display ${warn ? 'text-amber-300' : ''}`}>{display}</div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-semibold font-mono">{typeof value === 'number' ? value.toLocaleString() : value}</span>
        </div>
    );
}
